import { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import axios from "axios";
import BASE_URL from '../utils/apiConfig';

const OrderStatusPage = () => {
  const { orderId } = useParams();
  const location = useLocation();
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/orders/${orderId}`);
        setOrder(res.data);
        const urlParams = new URLSearchParams(location.search);
        const mpStatus = urlParams.get("status"); // puede ser approved, failure, pending
        setStatus(mpStatus || res.data.status || "unknown");
      } catch (error) {
        console.error("Error al obtener la orden:", error);
        setStatus("error");
      }
    };

    fetchOrder();
  }, [orderId, location.search]);

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-700">Cargando información de la orden...</p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white shadow-lg rounded-lg p-8 max-w-lg w-full">
          <div className="text-center">
            <div className="bg-red-100 p-3 rounded-full inline-flex mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Error al cargar la orden</h2>
            <p className="text-gray-600">No pudimos obtener la información del pedido. Por favor, intenta nuevamente más tarde.</p>
          </div>
        </div>
      </div>
    );
  }

  const getStatusColor = () => {
    if (status === "pending") return "bg-yellow-50 text-yellow-800 border-yellow-200";
    if (status === "cancelled" || status === "canceled") return "bg-red-50 text-red-800 border-red-200";
    return "bg-green-50 text-green-800 border-green-200";
  };

  const getStatusBadgeColor = () => {
    if (status === "pending") return "bg-yellow-100 text-yellow-800";
    if (status === "cancelled" || status === "canceled") return "bg-red-100 text-red-800";
    return "bg-green-100 text-green-800";
  };

  const getStatusMessage = () => {
    if (status === "approved" || status === "delivered") {
      return {
        title: "¡Gracias por tu compra!",
        message: "Tu pedido ha sido procesado correctamente.",
        icon: (
          <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        )
      };
    } else if (status === "pending") {
      return {
        title: "Tu pago está pendiente",
        message: "Estamos esperando la confirmación de tu pago.",
        icon: (
          <svg className="w-10 h-10 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        )
      };
    } else {
      return {
        title: "No pudimos procesar tu pago",
        message: "Hubo un error al procesar tu pago. Por favor, intenta nuevamente.",
        icon: (
          <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        )
      };
    }
  };

   // Función para formatear la personalización
   const formatCustomization = (customize) => {
    if (!customize) return "";
    
    const secondDashIndex = customize.indexOf('-', customize.indexOf('-') + 1);
    if (secondDashIndex !== -1) {
      // Retorna todo lo que viene DESPUÉS del segundo guión
      return customize.substring(secondDashIndex + 1);
    }
    return customize;
  };

  const statusInfo = getStatusMessage();

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Tarjeta principal */}
        <div className="bg-white shadow rounded-lg overflow-hidden mb-8">
          {/* Cabecera con estado */}
          <div className={`p-6 border-b ${getStatusColor()}`}>
            <div className="flex items-center">
              <div className="flex-shrink-0 mr-4">
                {statusInfo.icon}
              </div>
              <div>
                <h1 className="text-2xl font-bold">{statusInfo.title}</h1>
                <p className="text-lg">{statusInfo.message}</p>
              </div>
            </div>
          </div>

          {order && (
            <div className="p-6">
              {/* Número de pedido y fecha destacados */}
                <div className="flex flex-wrap items-center justify-between mb-8 bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500">Número de pedido</p>
                  <p className="text-lg font-bold">{order.id}</p>
                </div>

              <div className="flex flex-wrap items-center justify-between mb-8 bg-gray-50 p-4 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Fecha</p>
                  <p className="text-lg font-medium">{new Date(order.createdAt).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total</p>
                  <p className="text-lg font-bold">${parseFloat(order.total).toLocaleString("es-AR")}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Estado</p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${getStatusBadgeColor()}`}>
                    {status}
                  </span>
                </div>
              </div>

              {/* Datos del comprador */}
              <div className="mb-8">
                <h2 className="text-xl font-semibold mb-4 pb-2 border-b">Datos del Comprador</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Nombre completo</p>
                    <p className="font-medium">{order.shipping_address.buyerInfo.nombre} {order.shipping_address.buyerInfo.apellido}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{order.shipping_address.buyerInfo.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Teléfono</p>
                    <p className="font-medium">{order.shipping_address.buyerInfo.telefono}</p>
                  </div>
                </div>
              </div>

              {/* Método de envío */}
                <div className="mb-6">
                <h2 className="text-xl font-semibold mb-4 pb-2 border-b">Información de Entrega</h2>

                <div className="mb-4">
                    <h4 className="text-sm text-gray-500 mb-1">Método de Entrega</h4>
                    <p className="font-medium">
                        {order.shipping_address && order.shipping_address.type && 
                        order.shipping_address.type === "delivery" ? 'Envío' : "Retira en tienda"}
                    </p>
                    </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                    
                    
                    {order.shipping_address && order.shipping_address.type === "delivery" && 
                    order.shipping_address.buyerInfo && (
                    <div>
                        <h4 className="text-sm text-gray-500 mb-2">Dirección de envío</h4>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {(order.shipping_address.buyerInfo.calle || order.shipping_address.buyerInfo.altura) && (
                            <div>
                            <h5 className="text-xs text-gray-500">Dirección</h5>
                            <p className="font-medium">
                                {`${order.shipping_address.buyerInfo.calle || ''} ${order.shipping_address.buyerInfo.altura || ''} ${order.shipping_address.buyerInfo.depto ? `Depto ${order.shipping_address.buyerInfo.depto}` : ''}`}
                            </p>
                            </div>
                        )}

                        {order.shipping_address.buyerInfo.cp && (
                            <div>
                            <h5 className="text-xs text-gray-500">Código Postal</h5>
                            <p className="font-medium">{order.shipping_address.buyerInfo.cp}</p>
                            </div>
                        )}

                        {order.shipping_address.buyerInfo.provincia && (
                            <div>
                            <h5 className="text-xs text-gray-500">Provincia</h5>
                            <p className="font-medium">{order.shipping_address.buyerInfo.provincia}</p>
                            </div>
                        )}
                        
                        {order.shipping_address.buyerInfo.ciudad && (
                            <div>
                            <h5 className="text-xs text-gray-500">Localidad</h5>
                            <p className="font-medium">{order.shipping_address.buyerInfo.ciudad}</p>
                            </div>
                        )}
                        
                        {order.shipping_address.buyerInfo.observacion && (
                            <div className="col-span-1 md:col-span-2">
                            <h5 className="text-xs text-gray-500">Observaciones</h5>
                            <p className="font-medium">{order.shipping_address.buyerInfo.observacion}</p>
                            </div>
                        )}
                        </div>
                    </div>
                    )}
                </div>
                </div>

              {/* Productos */}
              <div>
                <h2 className="text-xl font-semibold mb-4 pb-2 border-b">Productos</h2>
                <ul className="divide-y divide-gray-200">
                  {order.item.map((item, index) => (
                    <li key={index} className="py-4">
                      <div className="flex flex-col sm:flex-row">
                        <div className="flex-shrink-0 sm:mr-4 mb-4 sm:mb-0">
                          <img
                            src={item.Product.image_url[0]}
                            alt={item.Product.name}
                            className="w-24 h-24 object-cover rounded-lg"
                          />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold">{item.Product.name}</h3>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                            <div>
                              <p className="text-sm text-gray-500">Precio</p>
                              <p className="font-medium">${parseFloat(item.price).toLocaleString("es-AR")}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Cantidad</p>
                              <p className="font-medium">{item.quantity}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Subtotal</p>
                              <p className="font-medium">${(parseFloat(item.price) * item.quantity).toLocaleString("es-AR")}</p>
                            </div>
                          </div>
                          {item.customize && (
                            <div className="mt-2">
                              <p className="text-sm text-gray-500">Personalización</p>
                              <p className="text-sm">{formatCustomization(item.customize)}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Botón de WhatsApp */}
          {order && (
            <div className="bg-gray-50 px-6 py-4 border-t">
              <a
                href={`https://wa.me/5493424365585?text=Hola! Quiero consultar sobre mi pedido con numero ${order.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-full sm:w-auto px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path d="M17.498 14.382c-.301-.15-1.767-.867-2.04-.966-.273-.101-.473-.15-.673.15-.197.295-.771.964-.944 1.162-.175.195-.349.21-.646.075-.3-.15-1.263-.465-2.403-1.485-.888-.795-1.484-1.77-1.66-2.07-.174-.3-.019-.465.13-.615.136-.135.301-.345.451-.523.146-.181.194-.301.297-.496.1-.21.049-.375-.025-.524-.075-.15-.672-1.62-.922-2.206-.24-.584-.487-.51-.672-.51-.172-.015-.371-.015-.571-.015-.2 0-.523.074-.797.359-.273.3-1.045 1.02-1.045 2.475s1.07 2.865 1.219 3.075c.149.195 2.105 3.195 5.1 4.485.714.3 1.27.48 1.704.629.714.227 1.365.195 1.88.121.574-.091 1.767-.721 2.016-1.426.255-.705.255-1.29.18-1.425-.074-.135-.27-.21-.57-.345m-5.446 7.443h-.016c-1.77 0-3.524-.48-5.055-1.38l-.36-.214-3.75.975 1.005-3.645-.239-.375a9.869 9.869 0 01-1.516-5.26c0-5.445 4.455-9.885 9.942-9.885a9.865 9.865 0 017.021 2.91 9.788 9.788 0 012.909 6.99c-.004 5.444-4.46 9.885-9.935 9.885M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.334.101 11.893c0 2.096.549 4.14 1.595 5.945L0 24l6.335-1.652c1.746.943 3.71 1.444 5.71 1.447h.006c6.585 0 11.946-5.336 11.949-11.896 0-3.176-1.24-6.165-3.495-8.411"/>
                </svg>
                Contactar por WhatsApp
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderStatusPage;